import { useState, useCallback } from 'react';
import { reorderLookups } from '../api/lookups';

interface UseReorderLookupsResult {
  reorder: (ids: number[]) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  reset: () => void;
}

/**
 * Hook for reordering lookup items
 */
export function useReorderLookups(
  type: string,
  onSuccess?: () => void
): UseReorderLookupsResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reorder = useCallback(
    async (ids: number[]): Promise<void> => {
      setIsLoading(true);
      setError(null);
      try {
        await reorderLookups(type, ids);
        onSuccess?.();
      } catch (err) {
        const apiError = err as { response?: { data?: { message?: string } } };
        const message = apiError.response?.data?.message || 'Failed to reorder items';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [type, onSuccess]
  );

  const reset = useCallback(() => {
    setError(null);
  }, []);

  return {
    reorder,
    isLoading,
    error,
    reset,
  };
}
